export const ROUTES = {
  onboarding: '/ptl/merchant-setup/onboarding',
  /** One onboarding step (pricing | tat | allocation) or the Done screen of one merchant. */
  onboardingStep: (merchantId, step) => `/ptl/merchant-setup/onboarding/${merchantId}/${step}`,
  update: '/ptl/merchant-setup/update',
  /** Merchant Update hub of one merchant (plan screen 12). */
  updateHub: (merchantId) => `/ptl/merchant-setup/update/${merchantId}`,
  /** One onboarding step of a live merchant in update mode (all steps open). */
  updateStep: (merchantId, step) => `/ptl/merchant-setup/update/${merchantId}/${step}`,
  zoneMap: '/ptl/merchant-setup/zone-map',
  zoneOverrides: '/ptl/merchant-setup/zone-map/overrides',
  zoneMapUpload: '/ptl/merchant-setup/zone-map/upload',
  zoneMapHistory: '/ptl/merchant-setup/zone-map/history',
  tatUpload: '/ptl/merchant-setup/tat/upload',
  tatCustom: '/ptl/merchant-setup/tat/custom',
  tatHistory: '/ptl/merchant-setup/tat/history',
  tat: '/ptl/merchant-setup/tat',
  /** Courier TAT list, and one courier's lanes / upload / history (?courier= prozoId). */
  tatCouriers: '/ptl/merchant-setup/tat/courier',
  tatCourier: (courier) => `/ptl/merchant-setup/tat/courier/lanes?courier=${encodeURIComponent(courier)}`,
  tatCourierUpload: (courier) => `/ptl/merchant-setup/tat/courier/upload?courier=${encodeURIComponent(courier)}`,
  tatCourierHistory: (courier) => `/ptl/merchant-setup/tat/courier/history?courier=${encodeURIComponent(courier)}`,
  /** Merchant details page (Merchant Setup → Merchants → a merchant); KYC documents are uploaded and approved on its Basic Details tab. */
  merchantDetails: (merchantId) => `/merchant-config/merchant/${merchantId}`,
  /** Its KYC Details card (Basic Details tab, #kyc scrolls to it). */
  merchantKyc: (merchantId) => `/merchant-config/merchant/${merchantId}?tab=0#kyc`,
  /** Merchant Setup → Pickup Locations (the page picks the merchant itself). */
  pickupLocations: '/merchant-config/pickup-locations',
}

/** Sections of the Merchant Zone Map page (PtlPage tabs). overrideCount: merchants with their own map, when known. */
export const zoneMapTabs = (overrideCount) => [
  { key: ROUTES.zoneMap, label: 'Standard Zone Map', to: ROUTES.zoneMap },
  { key: ROUTES.zoneOverrides, label: 'Zone Map Overrides', to: ROUTES.zoneOverrides, count: overrideCount },
]

/** Sections of the Merchant TAT page (PtlPage tabs). Counts (when known): couriers with a TAT, merchants on custom TAT. */
export const tatTabs = (customCount, courierCount) => [
  { key: ROUTES.tat, label: 'Standard TAT', to: ROUTES.tat },
  { key: ROUTES.tatCouriers, label: 'Courier TAT', to: ROUTES.tatCouriers, count: courierCount },
  { key: ROUTES.tatCustom, label: 'Custom TAT', to: ROUTES.tatCustom, count: customCount },
]

/** What a merchant's TAT is based on, for sentences: "the Prozo standard" or "the SAFEXPRESS TAT". */
export const tatBaseName = (courier) => (courier ? `the ${courier} TAT` : 'the Prozo standard')

/**
 * Onboarding steps, in order (D2C Merchant Setup → Onboarding pattern). Whether a step is done is read from the data
 * (MerchantStatus.stepStates), never stored. `help` is what the Help drawer shows for the step.
 */
export const ONBOARDING_STEPS = [
  {
    key: 'pricing',
    label: 'Pricing',
    title: 'PTL rate card',
    explainer: 'Upload the PTL rate card for each courier this merchant will use, one file per courier. Prices are read per lane (pickup zone + drop zone). TAT is not taken from the rate card: it is set in the next step.',
    help: [
      'One file per courier. Uploading again for the same courier replaces that courier\'s card for this merchant.',
      'A lane missing from the card gets no price, so PTL orders on that lane cannot be priced.',
      'Continue once at least one courier has a rate card.',
    ],
  },
  {
    key: 'tat',
    label: 'TAT',
    title: 'TAT: standard, courier or custom',
    explainer: 'Every PTL merchant starts on the Prozo standard TAT. Put it on a courier’s own TAT when it ships with that one courier only. Add custom lanes only where the agreement gives its own TAT; every other lane follows the standard or the courier TAT.',
    help: [
      'Nothing to do here if the agreement has no TAT terms: the Prozo standard applies to every lane.',
      'A courier TAT is uploaded once in Merchant TAT → Courier TAT and used by every merchant put on it. Lanes the courier has not given follow the standard.',
      'A custom TAT changes the EDD of new shipments on those lanes only.',
      'Every change is kept in the History (who, when, file). There is no rollback: to undo a change, upload the corrected file.',
    ],
  },
  {
    key: 'allocation',
    label: 'Allocation',
    title: 'Allocation couriers',
    explainer: 'Pick the couriers this merchant\'s PTL shipments may be allocated to, from the couriers named in the contract. Only couriers with a rate card for this merchant can be priced. With none picked, every pickup goes to the cheapest courier that has a rate card.',
    help: [
      'Select only couriers named in the commercial contract.',
      'If the contract does not name them (e.g. a blended rate card), ask the PTL BU head by email which couriers to map.',
      'Finish needs at least one rate card (Step 1). Picking couriers is optional: with none picked, the cheapest courier with a rate card is used.',
    ],
  },
]

/** Route key of the Done screen that follows the last step. */
export const DONE_STEP = 'done'

export const DONE_HELP = {
  title: 'Set-up complete',
  explainer: 'New PTL orders of this merchant are priced from its rate cards, get their TAT from the standard or its courier TAT (custom lanes on top) and are allocated to the couriers picked in Step 3.',
  help: [
    'Change rate cards, TAT or couriers later from Merchant Update. The same screens open there.',
    'The merchant uses the standard zone map. Give it its own map only if its contract maps pincodes differently.',
  ],
}

export const ZONE_MODES = {
  UPDATE: { label: 'Update changed pincodes', hint: 'Only the pincodes in the file change. Every other pincode stays as it is.' },
  REPLACE: { label: 'Replace full map', hint: 'The file becomes the whole map.' },
}

/** ODA Type values of the zone map file; anything else is not an ODA type (the upload rejects it). */
export const ODA_REGULAR = 'Regular'
export const ODA_TYPES = [ODA_REGULAR, 'ODA 1', 'ODA 2', 'ODA 3']

export const NOT_SERVICEABLE = '9999'

/** Version actions as ops read them. */
export const ACTION_LABEL = {
  UPLOAD: 'Upload', SAVE: 'Saved on screen', SWITCH_TO_STANDARD: 'Back to standard', TATBASIS: 'TAT basis changed', ROLLBACK: 'Rollback', EDIT: 'Changed on screen',
  UPLOAD_FAILED: 'Upload failed', SAVE_FAILED: 'Save failed', SWITCH_TO_STANDARD_FAILED: 'Back to standard failed',
}
/** Label of an audit log action ("upload", "switch_to_standard", "tatBasis", "upload_failed"; older records "rollback"). */
export const actionLabel = (a) => ACTION_LABEL[(a || '').toUpperCase()] || a

/** Upload check counts, in the order they are shown. */
export const COUNT_LABEL = {
  added: 'New',
  changed: 'Changed',
  unchanged: 'No change',
  removed: 'Removed',
  zoneChanges: 'Zone changed',
  pincodes: 'Pincodes after',
  lanes: 'Lanes',
  zones: 'Zones',
  customLanes: 'Custom lanes',
  courierLanes: 'Courier lanes',
  standardLanes: 'Standard lanes',
  cleared: 'Band cleared (fallback)',
  odaAdded: 'ODA added',
  odaRemoved: 'ODA removed (Regular)',
  odaTypeChanged: 'ODA type changed',
  withBand: 'With a band after',
  odaPincodes: 'ODA pincodes',
  newOda: 'New ODA pincodes',
  odaTypeAdded: 'ODA type added',
  odaTypeRemoved: 'ODA type removed (Regular)',
  bandSet: 'Band set',
  bandCleared: 'Band cleared',
  handDaysCleared: 'Days by hand cleared',
}

export const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '')

/** A date without time (agreement dates are days). YYYY-MM-DD is read as a local day, not UTC midnight, so no time zone shifts it. */
export const fmtDay = (d) => {
  if (!d) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d)
  const day = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(d)
  return day.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

export const num = (n) => (n || 0).toLocaleString('en-IN')
